import React, { useState, useRef, useEffect, useCallback } from 'react';
import './VisitUs.css';

function VisitUs() {
  const marketplaces = [
    {
      id: 1,
      name: 'Lazada',
      description: 'Shop our best-selling prints and exclusive online deals through our Lazada store.',
      iconPath: 'Lazada_Icon.png',
      iconSize: '50px',
      imagePath: 'lazada_section.png',
      link: 'https://www.lazada.com.ph/shop/jg-digital-printing',
      gradientClass: 'gradient-lazada',
    },
    {
      id: 2,
      name: 'Shopee',
      description: 'Enjoy promos, vouchers, and fast delivery when you order through our Shopee store.',
      iconPath: 'shopee_icon.png',
      iconSize: '40px',
      imagePath: 'shopee_section.png',
      link: 'https://shope.ee/5zyfPiYA8X',
      gradientClass: 'gradient-shopee',
    },
    {
      id: 3,
      name: 'TikTok Shop',
      description:
        'Catch our latest product demos and limited-time offers on TikTok Shop — follow and shop directly from our videos!',
      iconPath: 'TikTok_Shop_icon.png',
      iconSize: '50px',
      imagePath: 'tiktok_section.png',
      link: 'https://www.tiktok.com/@jgprints09?_t=8cM9Vufo4yl&_r=1',
      gradientClass: 'gradient-tiktok',
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef  = useRef(null);
  const slidesRef = useRef([]);   // refs to each slide element

  /* ── Get the width of one slide (including gap) for accurate scroll math ── */
  const getSlideWidth = useCallback(() => {
    const slide = slidesRef.current[0];
    if (!slide) return 0;
    // clientWidth + gap (12px default, 16px on ≥480 — read from computed style)
    const gap = parseInt(
      getComputedStyle(trackRef.current).gap || '16',
      10
    );
    return slide.offsetWidth + gap;
  }, []);

  /* Sync active dot when user swipes */
  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slideW = getSlideWidth();
    if (!slideW) return;
    const idx = Math.round(track.scrollLeft / slideW);
    setActiveIndex(Math.min(Math.max(idx, 0), marketplaces.length - 1));
  }, [getSlideWidth, marketplaces.length]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener('scroll', handleScroll, { passive: true });
    return () => track.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  /* Scroll programmatically when dot tapped */
  const scrollToIndex = useCallback((idx) => {
    const track = trackRef.current;
    if (!track) return;
    const slideW = getSlideWidth();
    track.scrollTo({ left: slideW * idx, behavior: 'smooth' });
    setActiveIndex(idx);
  }, [getSlideWidth]);

  /* ── Shared card renderer ── */
  /* ── Shared card renderer ── */
  const renderCard = (marketplace) => (
    <div className={`marketplace-card ${marketplace.gradientClass}`}>
      <div className="card-header">
        <img
          src={require(`../../assets/images/logos/${marketplace.iconPath}`)}
          alt={`${marketplace.name} icon`}
          className="marketplace-icon"
          style={{ width: marketplace.iconSize || 60, height: marketplace.iconSize || 60 }}
        />
        <h3 className="marketplace-name">{marketplace.name}</h3>
      </div>

      <p className="marketplace-description">{marketplace.description}</p>

      <div className="marketplace-illustration">
        <img
          src={require(`../../assets/images/logos/${marketplace.imagePath}`)}
          alt={`${marketplace.name} illustration`}
          className="illustration-image"
        />
      </div>

      <button
        className="visit-store-button"
        onClick={() => window.open(marketplace.link, '_blank')}
      >
        Visit Store
      </button>
    </div>
  );

  return (
    <section className="visit-us-section">
      <div className="visit-us-container">
        <h2 className="visit-us-title">Visit Us</h2>

        {/* ── Desktop: 3-column grid ── */}
        <div className="marketplace-grid vu-desktop-only">
          {marketplaces.map((mp) => (
            <div key={mp.id}>{renderCard(mp)}</div>
          ))}
        </div>

        {/* ── Mobile: center-focused carousel ── */}
        <div className="vu-carousel-wrapper vu-mobile-only">
          <div className="vu-carousel-track" ref={trackRef}>
            {marketplaces.map((mp, idx) => (
              <div
                key={mp.id}
                className="vu-carousel-slide"
                ref={el => (slidesRef.current[idx] = el)}
              >
                {renderCard(mp)}
              </div>
            ))}
          </div>

          <div className="vu-dots" role="tablist" aria-label="Marketplace slides">
            {marketplaces.map((mp, idx) => (
              <button
                key={mp.id}
                className={`vu-dot${idx === activeIndex ? ' active' : ''}`}
                onClick={() => scrollToIndex(idx)}
                aria-label={`Go to ${mp.name}`}
                role="tab"
                aria-selected={idx === activeIndex}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default VisitUs;